// frontend/src/utils/api.js
// -----------------------------------------------------------------------------
// Lightweight API client using browser-native fetch.
// Drop-in replacement for axios — returns { data, status } on success.
// Auth tokens are injected automatically by fetchInterceptor.js, so callers
// no longer need to pass Authorization headers manually.
// -----------------------------------------------------------------------------

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

/**
 * Core fetch wrapper.
 * @param {string} url
 * @param {object} opts
 * @param {string} [opts.method]
 * @param {*}      [opts.body]       - Will be JSON.stringify'd unless it's FormData
 * @param {object} [opts.headers]
 * @param {string} [opts.responseType] - 'blob' for file downloads
 * @returns {Promise<{data: any, status: number, headers: Headers}>}
 */
async function request(url, { method = 'GET', body, headers = {}, responseType, params } = {}) {
  // Auto-prefix relative paths with the backend base URL
  if (url.startsWith('/')) {
    url = API_BASE_URL + url;
  }

  // Append query-string parameters if provided
  if (params && typeof params === 'object') {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
    ).toString();
    if (query) {
      url += (url.includes('?') ? '&' : '?') + query;
    }
  }

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const fetchOpts = {
    method,
    headers: { ...headers },
  };

  if (body !== undefined && method !== 'GET') {
    if (isFormData) {
      // Let the browser set the correct multipart Content-Type + boundary
      fetchOpts.body = body;
    } else {
      fetchOpts.headers['Content-Type'] = 'application/json';
      fetchOpts.body = JSON.stringify(body);
    }
  }

  const response = await fetch(url, fetchOpts);

  // Parse response based on responseType
  let data;
  if (responseType === 'blob') {
    data = await response.blob();
  } else {
    data = await response.json().catch(() => null);
  }

  if (!response.ok) {
    const error = new Error(
      data?.message || data?.error || `Request failed with status ${response.status}`
    );
    error.response = { status: response.status, data };
    throw error;
  }

  return { data, status: response.status, headers: response.headers };
}

/**
 * api.get(url)
 * api.get(url, { responseType: 'blob' })
 * api.get(url, { headers: { ... } })
 */
function get(url, opts = {}) {
  return request(url, { method: 'GET', ...opts });
}

/**
 * api.post(url, body)
 * api.post(url, formData, { headers: { ... } })
 */
function post(url, body, opts = {}) {
  return request(url, { method: 'POST', body, ...opts });
}

/**
 * api.put(url, body)
 */
function put(url, body, opts = {}) {
  return request(url, { method: 'PUT', body, ...opts });
}

/**
 * api.patch(url, body)
 */
function patch(url, body, opts = {}) {
  return request(url, { method: 'PATCH', body, ...opts });
}

/**
 * api.delete(url)
 */
function del(url, opts = {}) {
  return request(url, { method: 'DELETE', ...opts });
}

const api = { get, post, put, patch, delete: del };
export default api;
export { API_BASE_URL };
